/**
 * useRouteAlerts
 *
 * Cloud-synced in-app departure reminders ("varsle X min før neste avgang").
 * Backed by `route-alerts` edge function. RLS denies anon client access.
 *
 * Architecture is push-ready: `last_triggered_departure_id` + `last_triggered_at`
 * provide deduplication keys that work for both in-app toasts and future push delivery.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { edgeFn } from '@/lib/edgeFn';
import { trackEvent } from '@/lib/analytics';
import { useLanguage } from '@/contexts/LanguageContext';

export type LeadTimeMinutes = 10 | 20 | 30;

export interface RouteAlert {
  id: string;
  samband_slug: string;
  lead_time_minutes: LeadTimeMinutes;
  /** Human-readable direction label, e.g. "Moss → Horten". `null` = both directions. */
  direction: string | null;
  active: boolean;
  last_triggered_at: string | null;
  last_triggered_departure_id: string | null;
  created_at: string;
  updated_at: string;
}

const CACHE_KEY = 'routeAlertsCache_v1';
const SYNC_EVENT = 'routeAlertsUpdated';

interface ListResponse { alerts: RouteAlert[] }
interface MutateResponse { alert: RouteAlert }

function readCache(): RouteAlert[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCache(alerts: RouteAlert[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(alerts));
    window.dispatchEvent(new CustomEvent(SYNC_EVENT));
  } catch { /* ignore quota */ }
}

export function useRouteAlerts() {
  const { t } = useLanguage();
  const [alerts, setAlerts] = useState<RouteAlert[]>(() => readCache());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const apply = useCallback((next: RouteAlert[]) => {
    if (!mountedRef.current) return;
    setAlerts(next);
    writeCache(next);
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await edgeFn<ListResponse>('route-alerts', {
        method: 'GET',
        anonAuth: true,
      });
      apply(data?.alerts ?? []);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      console.warn('[useRouteAlerts] fetch failed', err);
      setError(err instanceof Error ? err.message : t('route_alerts_fetch_failed'));
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [apply, t]);

  useEffect(() => {
    void fetchAlerts();
  }, [fetchAlerts]);

  // Cross-tab sync
  useEffect(() => {
    const sync = () => setAlerts(readCache());
    const handleStorage = (e: StorageEvent) => {
      if (e.key === CACHE_KEY) sync();
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener(SYNC_EVENT, sync);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(SYNC_EVENT, sync);
    };
  }, []);

  /**
   * `direction` is matched strictly: `null` only matches `null` (both-directions alert),
   * a string only matches that exact label.
   */
  const hasAlert = useCallback(
    (slug: string, leadTime: LeadTimeMinutes, direction: string | null = null) =>
      alerts.some(
        a =>
          a.samband_slug === slug &&
          a.lead_time_minutes === leadTime &&
          (a.direction ?? null) === direction,
      ),
    [alerts],
  );

  const alertsForRoute = useCallback(
    (slug: string) => alerts.filter(a => a.samband_slug === slug),
    [alerts],
  );

  const createAlert = useCallback(async (
    slug: string,
    leadTime: LeadTimeMinutes,
    direction: string | null = null,
  ): Promise<RouteAlert | null> => {
    try {
      const data = await edgeFn<MutateResponse>('route-alerts', {
        method: 'POST',
        body: { samband_slug: slug, lead_time_minutes: leadTime, direction },
        anonAuth: true,
      });
      if (data?.alert) {
        apply([data.alert, ...alerts.filter(a => a.id !== data.alert.id)]);
        trackEvent('route_alert_created' as Parameters<typeof trackEvent>[0], {
          route: slug,
          lead_time: leadTime,
          direction: direction ?? 'both',
        });
        return data.alert;
      }
      return null;
    } catch (err) {
      console.error('[useRouteAlerts] create failed', err);
      throw err;
    }
  }, [alerts, apply]);

  const toggleAlert = useCallback(async (id: string) => {
    const target = alerts.find(a => a.id === id);
    if (!target) return;
    try {
      const data = await edgeFn<MutateResponse>('route-alerts', {
        method: 'PATCH',
        body: { id, active: !target.active },
        anonAuth: true,
      });
      if (data?.alert) {
        apply(alerts.map(a => a.id === id ? data.alert : a));
        trackEvent('route_alert_toggled' as Parameters<typeof trackEvent>[0], { route: target.samband_slug, active: data.alert.active });
      }
    } catch (err) {
      console.error('[useRouteAlerts] toggle failed', err);
      throw err;
    }
  }, [alerts, apply]);

  const deleteAlert = useCallback(async (id: string) => {
    const target = alerts.find(a => a.id === id);
    if (!target) return;
    try {
      await edgeFn('route-alerts', {
        method: 'DELETE',
        body: { id },
        anonAuth: true,
      });
      apply(alerts.filter(a => a.id !== id));
      trackEvent('route_alert_deleted' as Parameters<typeof trackEvent>[0], { route: target.samband_slug });
    } catch (err) {
      console.error('[useRouteAlerts] delete failed', err);
      throw err;
    }
  }, [alerts, apply]);

  /** Mark an alert as having fired for a specific departure. Idempotent. */
  const markTriggered = useCallback(async (id: string, departureId: string) => {
    const triggeredAt = new Date().toISOString();
    // Optimistic update
    apply(alerts.map(a => a.id === id ? { ...a, last_triggered_at: triggeredAt, last_triggered_departure_id: departureId } : a));
    try {
      await edgeFn('route-alerts', {
        method: 'PATCH',
        body: {
          id,
          last_triggered_at: triggeredAt,
          last_triggered_departure_id: departureId,
        },
        anonAuth: true,
      });
    } catch (err) {
      console.warn('[useRouteAlerts] markTriggered failed (in-app trigger still recorded locally)', err);
    }
  }, [alerts, apply]);

  return {
    alerts,
    isLoading,
    error,
    hasAlert,
    alertsForRoute,
    createAlert,
    toggleAlert,
    deleteAlert,
    markTriggered,
    refresh: fetchAlerts,
  };
}
